import { Controller } from "@hotwired/stimulus";

import { resolveSkyPalette, SKY_THEMES, storedSkyPalette, weekCrowned, weekWarmth } from "../sky/palette.js";
import { createSkyRenderer } from "../sky/renderer.js";

const MAX_DPR = 2;
const RESIZE_DEBOUNCE_MS = 150;
const PALETTE_KEY = "peptide_tracker.sky_palette";

export default class extends Controller {
  static targets = ["back", "veil", "front", "grain", "swatch", "picker", "options", "header", "footer"];
  static values = { payload: Object };

  connect() {
    this.payload = this.payloadValue;
    this.selectedPalette = this.payload.palette;
    if (this.hasPickerTarget) {
      try {
        this.selectedPalette = storedSkyPalette(window.localStorage.getItem(PALETTE_KEY));
      } catch {
        this.selectedPalette = storedSkyPalette(this.selectedPalette);
      }
      this.renderOptions();
      this.pickerTarget.hidden = false;
    }
    this.motionPreference = window.matchMedia("(prefers-reduced-motion: reduce)");
    this.reduceMotion = this.motionPreference.matches;
    this.renderer = createSkyRenderer({
      back: this.backTarget,
      veil: this.veilTarget,
      front: this.frontTarget,
      grain: this.grainTarget,
    });
    this.markActiveSwatch();
    const painted = this.draw();
    this.onResize = () => {
      clearTimeout(this.resizeTimer);
      this.resizeTimer = setTimeout(() => this.draw(), RESIZE_DEBOUNCE_MS);
    };
    this.onMotionChange = (event) => {
      this.reduceMotion = event.matches;
      this.draw();
    };
    this.motionPreference.addEventListener("change", this.onMotionChange);
    window.addEventListener("resize", this.onResize);
    // A ResizeObserver reports once as soon as it starts observing. When the sky
    // has already painted, that report would only rebuild the same scene.
    let redundantReport = painted;
    this.resizeObserver = new ResizeObserver(() => {
      if (!redundantReport) this.onResize();
      redundantReport = false;
    });
    this.resizeObserver.observe(this.backTarget);
    if (this.hasHeaderTarget) this.resizeObserver.observe(this.headerTarget);
    if (this.hasFooterTarget) this.resizeObserver.observe(this.footerTarget);
  }

  disconnect() {
    window.removeEventListener("resize", this.onResize);
    this.motionPreference?.removeEventListener("change", this.onMotionChange);
    this.resizeObserver?.disconnect();
    clearTimeout(this.resizeTimer);
    this.renderer?.stop();
  }

  select(event) {
    const palette = event.params.palette;
    if (!SKY_THEMES.some((theme) => theme.id === palette)) return;
    this.selectedPalette = palette;
    try {
      window.localStorage.setItem(PALETTE_KEY, palette);
    } catch {
      // Browser storage can be unavailable while the current page still supports a preview.
    }
    this.markActiveSwatch();
    this.draw();
  }

  closePicker() {
    this.pickerTarget.open = false;
    this.pickerTarget.querySelector("summary").focus();
  }

  markActiveSwatch() {
    for (const swatch of this.swatchTargets) {
      swatch.setAttribute("aria-pressed", String(swatch.dataset.skyPaletteParam === this.selectedPalette));
    }
  }

  renderOptions() {
    this.optionsTarget.replaceChildren(
      ...SKY_THEMES.map((theme) => {
        const palette = resolveSkyPalette(theme.id);
        const button = document.createElement("button");
        button.type = "button";
        button.className =
          "group min-w-0 rounded-xl p-1.5 text-left ring-1 ring-inset ring-white/10 transition hover:bg-white/10 aria-pressed:bg-white/10 aria-pressed:ring-white/70 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white cursor-pointer";
        button.dataset.skyTarget = "swatch";
        button.dataset.action = "sky#select";
        button.dataset.skyPaletteParam = theme.id;
        button.setAttribute("aria-label", `${theme.label} sky. ${theme.description}`);
        const preview = document.createElement("span");
        preview.className = "relative isolate block h-20 overflow-hidden rounded-lg";
        preview.setAttribute("aria-hidden", "true");
        const canvases = Object.fromEntries(
          ["back", "veil", "front", "grain"].map((layer) => {
            const canvas = document.createElement("canvas");
            canvas.width = 180;
            canvas.height = 100;
            canvas.className = "absolute inset-0 h-full w-full";
            if (layer === "grain") canvas.className += " opacity-40 mix-blend-overlay";
            preview.append(canvas);
            return [layer, canvas];
          })
        );
        const renderer = createSkyRenderer(canvases);
        renderer.build({
          seed: "sky-palette-preview",
          beams: Array.from({ length: 6 }, (_, index) => ({ key: index, warmth: 0.9, crowned: false })),
          activityCount: 60,
          palette,
          tenureWeeks: 0,
          stars: [],
          starInsets: { top: 0, bottom: 0 },
          width: 180,
          height: 100,
          dpr: 1,
          still: true,
        });
        renderer.start();
        const label = document.createElement("span");
        label.className = "block px-1 pt-2 pb-1 text-sm font-medium";
        label.textContent = theme.label;
        button.append(preview, label);
        return button;
      })
    );
  }

  draw() {
    const dpr = Math.min(window.devicePixelRatio || 1, MAX_DPR);
    const cssWidth = this.backTarget.clientWidth || window.innerWidth;
    const cssHeight = this.backTarget.clientHeight || window.innerHeight;
    const width = Math.round(cssWidth * dpr);
    const height = Math.round(cssHeight * dpr);
    // A prerender or a hidden tab reports no layout size, and a zero-dimension
    // canvas makes the later glow blit throw, so nothing is drawn until the
    // canvas has real dimensions.
    if (width === 0 || height === 0) return false;
    for (const canvas of [this.backTarget, this.veilTarget, this.frontTarget, this.grainTarget]) {
      canvas.width = width;
      canvas.height = height;
    }
    const bounds = this.element.getBoundingClientRect();
    const palette = resolveSkyPalette(this.selectedPalette);
    this.element.style.backgroundColor = palette.sky.top;
    this.element.style.color = palette.ink;
    const beams = (this.payload.weeks ?? []).map((week) => ({
      key: week.week_start,
      warmth: weekWarmth(week.items),
      crowned: weekCrowned(week.items),
      doses: week.doses,
    }));
    this.renderer.build({
      seed: this.payload.seed,
      beams,
      activityCount: this.payload.activity_count,
      palette,
      tenureWeeks: this.payload.tenure_weeks,
      stars: this.payload.stars,
      starInsets: {
        top: this.hasHeaderTarget ? this.headerTarget.getBoundingClientRect().bottom - bounds.top + 24 : 120,
        bottom: this.hasFooterTarget ? bounds.bottom - this.footerTarget.getBoundingClientRect().top + 24 : 100,
      },
      width,
      height,
      dpr,
      still: this.reduceMotion,
    });
    this.renderer.start();
    return true;
  }
}
